import { localized, msg, str } from "@lit/localize";
import { html, LitElement, nothing, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { when } from "lit/directives/when.js";
import { quantityString } from "../locale";
import { InvalidConfigurationError, NotImplementedError } from "../utils";
import a11yStyles from "../a11y/styles.scss";
import style from './usos-selector.scss';
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";
import './text-field';
import "../extras/usos-spinner";
import { map } from "lit/directives/map.js";

type SelectorItem = AnyObject;

enum ResultLoadState {
    NOT_LOADED,
    FULL_RESULTS,
    PARTIAL_RESULTS,
    ERROR,
    LOADING,
}

/**
 * Search field simple popup list of results.
 */
@customElement('usos-selector')
@localized()
export class UsosSelector extends LitElement {
    static override styles = [a11yStyles, style];

    // section: properties related to pulling data

    /**
     * HTTP method for query.
     */
    @property({converter: (value) => value?.toLowerCase()})
    method: 'get' | 'post' = 'get';

    /**
     * Query URL.
     */
    @property()
    url: Optional<string> = null;

    /**
     * Form field name which exposes query string.
     */
    @property()
    name: Optional<string> = null;

    /**
     * Param for search query call. If null, {@link name} is used.
     */
    @property({attribute: 'search-param'})
    searchParam: Optional<string> = null;

    /**
     * Extra params to be passed in query. Must be URL-encoded string.
     */
    @property({attribute: 'extra-params'})
    extraParams: Optional<string> = null;

    /**
     * Key field of retrieved items, passed as key param of {@link onItemSelect} and {@link renderItem}.
     */
    @property({attribute: 'element-key'})
    elementKey = 'id';

    /**
     * Should query be cleared on focus out?
     */
    @property({attribute: 'clear-on-focus-out', type: Boolean})
    clearOnFocusOut = false;

    /**
     * Minimum number of characters to run query.
     */
    @property({attribute: 'min-search-length', type: Number})
    minSearchLength = 3;

    /**
     * Value of search field.
     */
    @property({attribute: 'value'})
    value = '';

    /**
     * Delay in milliseconds between last keypress and query start.
     */
    @property({attribute: 'update-delay', type: Number})
    updateDelay = 1500;

    /**
     * Allow also empty search query.
     */
    @property({attribute: 'allow-empty', type: Boolean})
    allowEmpty = false;

    // section: labels and hints

    /**
     * @deprecated Use labels and supporting text
     */
    @property()
    placeholder = '';

    /**
     * Label for text field.
     */
    @property()
    label = '';

    /**
     * Conveys additional information below the text field, such as how it should
     * be used.
     */
    @property({attribute: 'supporting-text'})
    supportingText = '';

    /**
     * Should we use "xl" mode of {@link TextField}?
     */
    @property({type: Boolean})
    xl = false;

    /**
     * Text shown as hint in results box when query was not started. Defaults to prompt for {@link minSearchLength} character input.
     */
    @property()
    hint = '';

    // section: properties related to sending data

    /**
     * Allow submitting external form after selecting item.
     */
    @property({attribute: 'allow-external-form', type: Boolean}) allowExternalForm = false;

    // section: methods

    /**
     * Called when item was selected from results.
     * @param item Selected item (as returned from itemsSelector)
     * @param key Key for item (elementKey)
     */
    onItemSelect: (item: SelectorItem, key: unknown) => void = () => { throw new NotImplementedError('onItemSelect was not configured'); };

    /**
     * Render item to HTML.
     * @param item Selected item (as returned from itemsSelector)
     * @param key Key for item (elementKey)
     * @param templateTag Tag function used to build Lit-compatible template result, used like ``templateTag`<p>...</p>` ``
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals#tagged_templates
     * @returns Either item presentation as string, possibly with HTML tags, or template result
     */
    @property({attribute: false})
    renderItem: (item: SelectorItem, key: unknown, templateTag: typeof html) => string | ReturnType<typeof html> = (item) => html`${item.match}`;

    /**
     * Select items from query response.
     * @param response query response object
     * @returns list of items to show to user
     */
    itemsSelector: (response: AnyObject) => SelectorItem[] = (response) => response.items as AnyObject[];

    /**
     * Choose whether there are more results than to be shown to user.
     * @param response query response object
     * @returns true if there are more results
     */
    hasMoreItems: (response: AnyObject) => boolean = (response) => response.next_page as boolean;

    // section: wire code
    private _searchingTimeout: Optional<ReturnType<typeof setTimeout>> = null;

    private _cache = new Map<string, {items: SelectorItem[], hasMore: boolean}>();

    @state()
    private items: SelectorItem[] = [];

    @state()
    private selectedItemId = -1;

    @state()
    private loadState: ResultLoadState = ResultLoadState.NOT_LOADED;

    @state()
    private isFocused = false;

    static formAssociated = true;
    private _internals: ElementInternals;

    constructor() {
        super();
        this._internals = this.attachInternals();
    }

    // section: render and event handlers

    override render() {
        const hiddenStyle = this.isFocused ? {} : {display: 'none'};
        /* eslint-disable @typescript-eslint/no-deprecated */
        return html`
            <span class="sr-only" id="selector-label">${this.label || this.placeholder}</span>
            <text-field id="selector" tabindex=0 type="text" .xl=${this.xl}
                .initialValue=${this.value} .label=${this.label || this.placeholder} .supportingText=${this.supportingText}
                aria-owns="results-list" aria-controls="results-list" aria-expanded=${this.isFocused}
                aria-autocomplete="list" role="combobox" aria-labelledby="selector-label"
                aria-activedescendant=${this.selectedItemId != -1 ? `option-${this.selectedItemId}` : nothing}
                @focus=${this._onFocus} @blur=${this._onBlur} @input=${this._onInput} @keydown=${this._onKeydown}
                ></text-field>
            ${when(this.loadState == ResultLoadState.LOADING, () => html`
                <usos-spinner id="loading"></usos-spinner>
            `)}
            <div id="results-popup" style=${styleMap(hiddenStyle)} @mousedown=${this._preventUnfocus} aria-live="polite">
                <div id="results">
                    ${when(this.items.length > 0, () => this._renderItems(this.isFocused))}
                </div>
                <div id="hint" ?data-error=${this.loadState == ResultLoadState.ERROR}>${this._renderHint()}</div>
            </div>
        `;
        /* eslint-enable @typescript-eslint/no-deprecated */
    }

    private _renderHint(): string {
        const state = this.allowEmpty && this.value.length == 0 ? ResultLoadState.NOT_LOADED : this.loadState;
        switch (state) {
            case ResultLoadState.ERROR:
                return msg(str`Nie udało się pobrać danych`);
            case ResultLoadState.NOT_LOADED: {
                const charsHintPart = quantityString({one: msg(str`znak`), few: msg(str`znaki`), many: msg(str`znaków`), other: msg(str`znaków`)}, this.minSearchLength);
                const hint = (this.hint != '') ? this.hint : msg(str`Wpisz co najmniej ${this.minSearchLength} ${charsHintPart}, aby rozpocząć wyszukiwanie`);
                return hint;
            }
            case ResultLoadState.PARTIAL_RESULTS:
                return msg(str`Liczba trafień: ${this.items.length} lub więcej`);
            case ResultLoadState.FULL_RESULTS:
                return msg(str`Liczba trafień: ${this.items.length}`);
            case ResultLoadState.LOADING:
                return msg(str`Ładowanie...`);
        }
    }

    private _renderItems(isExpanded: boolean): TemplateResult {
        // We disable keyboard accessibility lints here, because we handle keyboard navigation on the text field,
        // and we don't want to allow focusing items in the list - they are not interactive elements, but
        // clicking them should select them without moving focus from the text field.
        /* eslint-disable lit-a11y/mouse-events-have-key-events */
        /* eslint-disable lit-a11y/click-events-have-key-events */
        return html`
            <span class="sr-only" id="results-label">${msg(str`Wyniki wyszukiwania`)}</span>
            <ul aria-expanded=${isExpanded} id="results-list" role="listbox" aria-labelledby="results-label">
                ${map(this.items, (item, index) => html`
                    <li id="option-${index}" ?data-selected=${this.selectedItemId == index} data-index=${index}
                        @click=${this._onItemClick} @mouseover=${this._onMouseOver}>
                        ${this._renderListItem(item)}
                    </li>
                `)}
            </ul>
        `;
        /* eslint-enable lit-a11y/mouse-events-have-key-events */
        /* eslint-enable lit-a11y/click-events-have-key-events */
    }

    private _renderListItem(item: AnyObject) {
        const rendered = this.renderItem(item, item[this.elementKey], html);
        return (typeof rendered === 'string') ? unsafeHTML(rendered) : rendered;
    }

    private _preventUnfocus(e: Event) {
        e.preventDefault();
    }

    private _onFocus() {
        this.isFocused = true;
        this._searchingTimeout = setTimeout(() => void this.downloadData(), this.updateDelay);
    }

    private _onBlur() {
        this._clear(false);
    }

    private _clear(force: boolean) {
        this.isFocused = false;
        if (this._searchingTimeout != null)
            clearTimeout(this._searchingTimeout);
        if (this.clearOnFocusOut || force) {
            this.value = '';
        }
        this._searchingTimeout = null;
    }

    private _onMouseOver(e: Event) {
        const index = Number((e.target as HTMLElement).dataset.index);
        if (0 <= index && index < this.items.length)
            this.selectedItemId = index;
    }

    private _onInput(e: Event) {
        this.isFocused = true;
        this.value = (e.target as HTMLInputElement).value;
        this._internals.setFormValue(this.value);
        if (this.value.length < this.minSearchLength) {
            this.loadState = ResultLoadState.NOT_LOADED;
            this.items = [];
        }
    }

    private _onKeydown(e: KeyboardEvent) {
        const shouldUpdate = !/Arrow|Page|Esc|Enter|Shift|Meta|Alt|Control|Tab|Del|Home|End/.test(e.key);
        if (shouldUpdate) {
            if (this._searchingTimeout != null)
                clearTimeout(this._searchingTimeout);
            this._searchingTimeout = setTimeout(() => void this.downloadData(), this.updateDelay);
        } else if (e.key == 'Enter') {
            if (this.selectedItemId != -1) {
                const item = this.items[this.selectedItemId];
                this._onItemSelect(item, item[this.elementKey]);
            } else {
                const submitButton = this._internals.form?.querySelector('input[type=submit], button[type=submit]') as Optional<HTMLInputElement | HTMLButtonElement>;
                if (this.allowExternalForm && submitButton !== null) {
                    this._internals.form?.requestSubmit(submitButton);
                } else {
                    this._internals.form?.requestSubmit();
                }
            }
        } else if (e.key == 'ArrowUp') {
            e.preventDefault(); // disallow moving cursor on arrow up and down
            this.selectedItemId = this.selectedItemId >= 0 ? this.selectedItemId - 1 : -1;
        } else if (e.key == 'ArrowDown') {
            e.preventDefault(); // disallow moving cursor on arrow up and down
            this.selectedItemId = this.selectedItemId < this.items.length - 1 ? this.selectedItemId + 1 : this.selectedItemId;
        }
    }

    private _onItemClick(e: Event) {
        const index = Number((e.currentTarget as HTMLElement).dataset.index);
        if (0 <= index && index < this.items.length) {
            const item = this.items[index];
            this._onItemSelect(item, item[this.elementKey]);
        }
    }

    private _onItemSelect(item: SelectorItem, key: unknown){
        this.onItemSelect(item, key);
        this._clearSearch();
    }

    // section: data loading and processing

    private async downloadData() {
        const value = this.value;
        if (value.length < this.minSearchLength && !(value.length == 0 && this.allowEmpty))
            return;
        const cacheItem = this._cache.get(value);
        if (cacheItem) {
            this.items = cacheItem.items;
            this.loadState = cacheItem.hasMore ? ResultLoadState.PARTIAL_RESULTS : ResultLoadState.FULL_RESULTS;
            return;
        }

        const requestParams = this.constructRequest(value);
        try {
            this.loadState = ResultLoadState.LOADING;
            const response = await fetch(requestParams.url, {
                method: this.method,
                headers: (this.method == 'post') ? {'Content-Type': 'application/x-www-form-urlencoded'} : {},
                body: requestParams.data,
            });
            if (response.ok) {
                const data = await response.json() as AnyObject;
                const items = this.itemsSelector(data);
                const hasMore = this.hasMoreItems(data);
                this.items = items;
                this.loadState = hasMore ? ResultLoadState.PARTIAL_RESULTS : ResultLoadState.FULL_RESULTS;
                this._cache.set(value, {items, hasMore});
            } else {
                this.loadState = ResultLoadState.ERROR;
            }
        } catch {
            this.loadState = ResultLoadState.ERROR;
        } finally {
            this.selectedItemId = -1;
        }
    }

    private constructRequest(value: string): {url: string, data: string | null} {
        if (this.url == null || this.url == '')
            throw new InvalidConfigurationError('URL was not set');
        if ((this.name == null || this.name == '') && (this.searchParam == null || this.searchParam == ''))
            throw new InvalidConfigurationError('Name was not set');
        const searchParam = (this.searchParam ?? this.name)!; // definitely not-null from above if statement

        if (this.method == 'get') {
            const url = new URL(this.url);
            if (url.search != '')
                url.search += '&';
            url.search += `${encodeURIComponent(searchParam)}=${encodeURIComponent(value)}`;
            if (this.extraParams != null && this.extraParams != '')
                url.search += '&' + this.extraParams;
            return {url: url.href, data: null};
        } else { // if (this.method == 'post')
            let data = `${encodeURIComponent(searchParam)}=${encodeURIComponent(value)}`;
            if (this.extraParams != null && this.extraParams != '')
                data += '&' + this.extraParams;
            return {url: this.url, data: data};
        }
    }

    // section: utilities

    /**
     * Configure selector to send {@link elementKey} value as query param on item select.
     * @param paramName name of query param
     */
    public selectItemAsQueryParam(paramName: string) {
        this.onItemSelect = (_, key) => {
            const data = new FormData();
            data.append(paramName, String(key));
            this._internals.setFormValue(data);
            this._internals.form?.requestSubmit();
        }
    }

    @query('#selector') private selector!: HTMLInputElement;

    private _clearSearch(): void {
        this.selectedItemId = -1;
        this.selector.value = '';
        this._clear(true);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "usos-selector": UsosSelector,
    }
}
